// „Wer liest, holt": Abruf, Takt und Push-Abo eines Stores nur, solange
// jemand die Daten ansieht (docs/data-loading.md › Boot-Pattern, Ausnahme).
//
// Der erste Beobachter holt sofort (außer der letzte erfolgreiche Abruf ist
// jünger als `frisch`) und startet Takt und Abo, der letzte beendet beides. `aktiv()` sagt, ob jemand zusieht -- der Revalidator des
// Stores (Fokuswechsel, Wiederverbinden) und seine WS-Rahmen fragen es, bevor
// sie laden. Ohne Beobachter kostet der Store nichts außer dem
// SWR-Zwischenspeicher, den `start*` einspielt.
//
// Für Stores, deren Daten nur EINE Seite (oder eine kleine Gruppe) liest:
// Logs, AdGuard, Lightning, Vault, Sicherheit (Ereignisse), Zigbee/ESPHome/
// Automatiken (Navigationssuche). Die älteren Kopien dieses Musters
// (runs-summary, netsec-summary, Query-Log in adguard-summary) zählen ihre
// Beobachter selbst und sind absichtlich nicht umgebaut.
//
// Reine TypeScript-Datei ohne Runen, damit sie unter node getestet werden
// kann (bei-bedarf.test.mjs). In Svelte reicht
//   $effect(() => { if (zuSehen()) return beobachteX(); })
// -- der Store umschließt den Aufruf mit `untrack`.

export interface BeiBedarfOptionen {
  /** Der Abruf. Läuft sofort für den ersten Beobachter und dann im Takt. Als
   *  erfolgreich gilt er, wenn er ohne Fehler zurückkehrt (bzw. sein Versprechen
   *  erfüllt wird). */
  hole: () => void | Promise<void>;
  /** Takt in Millisekunden. Fehlt er, gibt es keinen (nur Abo und Sofort-Abruf). */
  takt?: number;
  /** Push-Abo, das nur mit Beobachter besteht; die Rückgabe meldet es ab. Wirft
   *  es, läuft der Rest (Abruf, Takt, Zählung) trotzdem. */
  abo?: () => () => void;
  /** Millisekunden: ist der letzte erfolgreiche Abruf jünger, holt der erste
   *  Beobachter nicht sofort (der Takt startet trotzdem). Für Stores, deren
   *  Beobachter oft kommen und gehen (die Navigationssuche wird beim Tippen und
   *  Leeren neu gebaut). Fehlt es, holt er immer. */
  frisch?: number;
}

export interface BeiBedarf {
  /** Anmelden; die Rückgabe meldet ab (mehrfaches Abmelden zählt einmal). */
  beobachte: () => () => void;
  /** Sieht gerade jemand zu? */
  aktiv: () => boolean;
  /** Alles beenden und die Zählung auf null setzen (stop* des Stores). */
  beende: () => void;
}

export function beiBedarf(opt: BeiBedarfOptionen): BeiBedarf {
  let anzahl = 0;
  let timer: ReturnType<typeof setInterval> | undefined;
  let abmelden: (() => void) | undefined;
  // Jede Anmeldung bekommt eine Nummer; beende() hebt die Epoche, damit ein
  // Abmelden aus der Zeit davor die neue Zählung nicht verfälscht.
  let epoche = 0;
  // Zeitpunkt des letzten erfolgreichen Abrufs (für `frisch`).
  let erfolg: number | undefined;

  const hole = (): void => {
    const meine = epoche;
    const gelungen = (): void => {
      // Ein Abruf aus der Zeit vor beende() sagt über die neue nichts.
      if (meine === epoche) erfolg = Date.now();
    };
    try {
      const r = opt.hole();
      if (r && typeof (r as Promise<void>).then === "function") {
        (r as Promise<void>).then(gelungen, () => {});
      } else {
        gelungen();
      }
    } catch {
      // der nächste Takt versucht es wieder
    }
  };

  const istFrisch = (): boolean =>
    opt.frisch !== undefined && erfolg !== undefined && Date.now() - erfolg < opt.frisch;

  const raeume = (): void => {
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
    if (abmelden) {
      const ab = abmelden;
      abmelden = undefined;
      try {
        ab();
      } catch {
        // die Leitung ist ohnehin weg
      }
    }
  };

  return {
    beobachte() {
      const meine = epoche;
      let offen = true;
      anzahl++;
      if (anzahl === 1) {
        // Abo zuerst, damit zwischen Abruf und Abo kein Push verloren geht.
        if (opt.abo) {
          try {
            abmelden = opt.abo();
          } catch {
            // ohne Push bleiben Abruf und Takt
          }
        }
        if (!istFrisch()) hole();
        if (opt.takt) timer = setInterval(hole, opt.takt);
      }
      return () => {
        if (!offen || meine !== epoche) return;
        offen = false;
        anzahl--;
        if (anzahl === 0) raeume();
      };
    },
    aktiv: () => anzahl > 0,
    beende() {
      epoche++;
      anzahl = 0;
      erfolg = undefined;
      raeume();
    },
  };
}
